/**
 * RouteError — root route ErrorBoundary for the shop SPA.
 *
 * Purpose: catch any uncaught render/runtime error in a route subtree and show
 *   a branded, recoverable message instead of React Router's raw developer
 *   crash screen (or a white page). Keeps the storefront usable if one route
 *   throws (e.g. an unexpected API payload shape).
 * Inputs: none (reads the active route error via useRouteError()).
 * Outputs: a fallback UI with a reload action.
 * Constraints: must not itself depend on data fetching or context that could throw.
 */
import { useRouteError, isRouteErrorResponse, Link } from 'react-router'

export function RouteError() {
  const error = useRouteError()
  const is404 = isRouteErrorResponse(error) && error.status === 404

  // A 404 (unmatched route or loader not-found) is not a crash — give it a
  // proper "page not found" experience rather than the "something broke" copy.
  if (is404) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <p className="text-gold text-6xl font-extrabold tracking-tight mb-2">404</p>
        <h1 className="text-3xl font-bold mb-3">Page not found</h1>
        <p className="text-white/60 mb-8">
          That page doesn’t exist or may have moved. Let’s get you back on track.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Link
            to="/products"
            className="inline-block bg-gold text-white font-semibold px-6 py-2.5 rounded-md hover:bg-gold-bright transition-colors"
          >
            Shop all gear
          </Link>
          <Link
            to="/design"
            className="inline-block border border-gold/40 text-white px-6 py-2.5 rounded-md hover:bg-navy/40 transition-colors"
          >
            Build your own
          </Link>
          <Link
            to="/"
            className="inline-block text-white/70 px-4 py-2.5 hover:text-white transition-colors"
          >
            Go home
          </Link>
        </div>
      </div>
    )
  }

  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : 'An unexpected error occurred.'

  return (
    <div className="max-w-2xl mx-auto px-6 py-20 text-center">
      <h1 className="text-3xl font-bold mb-3">Something went wrong</h1>
      <p className="text-white/60 mb-2">
        We hit a snag loading this page. Please try again.
      </p>
      <p className="text-white/30 text-xs mb-8 break-words">{message}</p>
      <div className="flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-block bg-gold text-white font-semibold px-6 py-2.5 rounded-md hover:bg-gold-bright transition-colors"
        >
          Reload
        </button>
        <Link
          to="/"
          className="inline-block border border-gold/40 text-white px-6 py-2.5 rounded-md hover:bg-navy/40 transition-colors"
        >
          Go home
        </Link>
      </div>
    </div>
  )
}
